import { useCallback, useLayoutEffect, useRef } from 'react';
import {
	useObservableRef,
	useObserveRef,
	isNumber,
	constrain,
} from '@chkp/mwc-common';

const MAX = Number.MAX_SAFE_INTEGER;

const calcColumnWidths = (columns, availableWidth) => {
	let remainingWidth = availableWidth;
	let remainingFlex = 0;
	const widths = Array(columns.length).fill(null);

	// Calculate static widths, and sum all flex
	columns.forEach(({ width, minWidth = 0, maxWidth = MAX, flex }, idx) => {
		if (isNumber(width)) {
			const finalWidth = constrain(width, minWidth, maxWidth);
			widths[idx] = finalWidth;
			remainingWidth -= finalWidth;
		} else if (isNumber(flex)) {
			remainingFlex += flex;
		} else {
			widths[idx] = 0;
		}
	});

	// Distribute remaining width between flex items.
	// The distribution is not even, because items have minWidth & maxWidth constrains
	widths.forEach((width, idx) => {
		if (width === null) {
			const { flex, minWidth, maxWidth = MAX } = columns[idx];
			if (remainingWidth > 0) {
				const flexWidth = remainingWidth * (flex / remainingFlex);
				const width = constrain(
					flexWidth,
					minWidth || 0,
					Math.min(maxWidth, remainingWidth),
				);
				widths[idx] = width;
				remainingWidth -= width;
				remainingFlex -= flex;
			} else {
				// Use flex item's minWidth as its width
				widths[idx] = minWidth || 0;
			}
		}
	});

	// Last column must take the rest of the available space
	const finalWidth = widths.reduce((sum, w) => sum + w, 0);
	if (finalWidth < availableWidth) {
		widths[columns.length - 1] += availableWidth - finalWidth;
	}

	return widths;
};

/**
 * Add data & methods for real-time imperative resizing:
 * -  Calculate each column's finalWidth
 * -  Add 'resize' method to each column (for column resizing)
 * -  Add 'update' method to the columns array, which recalculates column widths and notifies columnsObservable (for grid resizeng)
 * @param columnsConfig
 * @param columnsObservable
 * @param contentWidthObservable
 * @returns {*}
 */
const decorateColumns = (
	columnsConfig,
	columnsObservable,
	contentWidthObservable,
) => {
	const columns = columnsConfig.map((columnConfig, idx) => ({
		...columnConfig,
		idx,
		resize(width) {
			this.width = width;
			columns.update();
		},
	}));

	columns.update = () => {
		const widths = calcColumnWidths(
			columns,
			contentWidthObservable.current || 0,
		);

		// Total minimum width of columns (minimal desired width)
		columns.totalWidth = columns.reduce(
			(acc, col) =>
				acc + constrain(col.width || 0, col.minWidth || 0, col.maxWidth),
			0,
		);

		columns.forEach((column, idx) => {
			// eslint-disable-next-line no-param-reassign
			column.finalWidth = widths[idx];
		});

		// Final width of columns
		// columns.finalWidth = columns.reduce((acc, col) => acc + col.finalWidth, 0);

		// console.log(
		// 	'columns',
		// 	columns.map((c) => c.finalWidth),
		// 	columns.totalWidth,
		// );

		columnsObservable.notify();
	};

	columns.update();
	return columns;
};

export const useColumnsObservable = (columnsConfig, contentWidthObservable) => {
	const columnsObservable = useObservableRef();

	const resetColumns = () => {
		if (columnsConfig) {
			columnsObservable.current = decorateColumns(
				columnsConfig,
				columnsObservable,
				contentWidthObservable,
			);
		} else {
			columnsObservable.current = null;
		}
	};

	// Initialize columns immediately

	// Initialize columns when columns change
	const isFirstRef = useRef(true);
	isFirstRef.current && resetColumns();
	useLayoutEffect(
		() => {
			if (!isFirstRef.current) {
				resetColumns();
			} else {
				isFirstRef.current = false;
			}
		},
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
		[columnsConfig],
	);

	// update column widths when content-width changes
	useObserveRef(contentWidthObservable, () => {
		columnsObservable.current?.update();
	});

	return columnsObservable;
};
